import {
  Button,
  Col,
  DatePicker,
  Form,
  Popconfirm,
  Row,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setModal } from '../../../common/slices/commonSlice';
import { IPermission, crud } from '../../../common/types/commonTypes';
import CheckImageOrPdf from '../../../common/utils/CheckImageOrPdf';
import { debounce, rangePresets } from '../../../common/utils/common.utils';
import usePagination from '../../../common/utils/usePagination';
import useUrlParams from '../../../common/utils/useUrlParams';
import BreadCrumb from '../../../components/common/breadCrumb/BreadCrumb';
import { FormInput } from '../../../components/common/FormItem/FormItems';
import Reload from '../../../components/common/reload/Reload';
import { loadingIndicator } from '../../../components/common/spinner/LoadingIndicator';
import { formatDateRangeArray, parseDateRangeObject } from '../../../helpers/healper';
import {
  useDeleteEmployeeExpenseMutation,
  useGetAllPayrollQuery,
  useLazyGetForEditPayrollQuery,
} from '../api/endpoints/payrollEndpoints';
import AddEmployeeExpense from '../modals/AddPayroll';
import { IPayrollGetAll } from '../types/PayrollTypes';
type Props = { permission?: IPermission | undefined };

export default function Payroll({ permission }: Props) {
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };
  const [columnIndex, setColumnIndex] = useState<number | null>(null);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const [editOrAdd, setEditOrAdd] = useState<{
    reason: 'ADD' | 'EDIT';
    id?: number;
  }>({ reason: 'ADD' });

  const {
    data,
    refetch: getAllRefetch,
    isLoading: dataIsLoading,
  } = useGetAllPayrollQuery({
    ...getAllUrlParams(),
    page,
    size,
  });

  // Add safety check for data mapping
  const dataWithKey =
    data?.data?.map((item, index) => ({
      key: String(index),
      ...item,
    })) || [];

  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search || '',
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);

  // GET FOR EDIT
  const [getForEditPayroll, { data: getForEditData, isFetching, isSuccess, error, isLoading }] =
    useLazyGetForEditPayrollQuery();

  // DELETE
  const [
    deleteEmployeeExpense,
    {
      isLoading: deleteIsLoading,
      isSuccess: deleteIsSuccess,
      data: deleteData,
      isError: deleteIsError,
    },
  ] = useDeleteEmployeeExpenseMutation();
  useEffect(() => {
    if (deleteIsSuccess) {
      setColumnIndex(null);
      message.success(deleteData?.message || 'Deleted');
    } else if (deleteIsError) {
      setColumnIndex(null);
    }
  }, [deleteIsSuccess, deleteIsError]);
  useEffect(() => {
    if (deleteIsError) {
      message.error('Delete faild!');
    }
  }, [deleteIsError]);
  // HANDLE RESTORE BUTTON
  const [checkButtonState, setCheckButtonState] = useState(false);

  const columns: ColumnsType<IPayrollGetAll> = [
    {
      title: 'Sl.',
      render: (_, data, index) => (
        <>{((pagination.current || 1) - 1) * (pagination.pageSize || 20) + 1 + index}</>
      ),
      align: 'center',
    },
    {
      title: 'Vouchar No.',
      dataIndex: 'payroll_vouchar_no',
      key: 'payroll_vouchar_no',
    },
    {
      title: 'Salary Date',
      dataIndex: 'payroll_date',
      key: 'payroll_date',
      render: (_, __) => <>{dayjs(__.payroll_date).format('DD-MM-YYYY')}</>,
    },
    {
      title: 'Employee Name',
      dataIndex: 'employee_full_name',
      key: 'employee_full_name',
    },

    {
      title: 'Designation',
      dataIndex: 'designation_name',
      key: 'designation_name',
    },

    {
      title: 'Pay. Method',
      dataIndex: 'payroll_pay_method',
      key: 'payroll_pay_type',
    },

    {
      title: 'Account Name',
      dataIndex: 'bank_account_name',
      key: 'bank_account_name',

      render: (_, record) => {
        let color = record.bank_account_name === 'DEPOSIT' ? 'green' : 'red';
        function status(record: any) {
          if (
            record.bank_account_name == 'DEPOSIT' ||
            record.bank_account_name == 'PENDING' ||
            record.bank_account_name == 'BOUNCE'
          ) {
            return <span style={{ color }}> {record.bank_account_name} </span>;
          } else {
            return `${record.bank_account_name}`;
          }
        }

        return status(record);
      },
    },
    {
      title: 'Base Salary',
      dataIndex: 'payroll_salary',
      key: 'payroll_salary',
      align: 'right',
      render: (_, data) => <>{Number(data.payroll_salary)}</>,
    },
    {
      title: 'Attendance',
      dataIndex: 'payroll_attendance',
      key: 'payroll_attendance',
      align: 'right',
    },
    {
      title: 'Gross Salary',
      dataIndex: 'gross_salary',
      key: 'gross_salary',
      align: 'right',
      render: (_, data) => <>{Number(data.gross_salary)}</>,
    },
    {
      title: 'Total Salary',
      dataIndex: 'payroll_net_amount',
      key: 'payroll_net_amount',
      align: 'right',
      render: (_, data) => <>{Number(data.payroll_net_amount)}</>,
    },
    {
      title: 'Image',
      dataIndex: 'payroll_image_url',
      key: 'payroll_image_url',
      render: (_, data) =>
        data.payroll_image_url && <CheckImageOrPdf url={String(data.payroll_image_url) || ''} />,
    },

    {
      title: 'Actions',
      render: (_, __, index) => {
        return (
          <>
            <Space size='small'>
              <Link to={`/payroll/${__.payroll_id}`} state={location.pathname}>
                {!checkButtonState && permission?.['read:any'] && (
                  <Button size='small' type='primary'>
                    <Typography style={{ color: '#ffffff' }}>View</Typography>
                  </Button>
                )}
              </Link>

              {permission?.[crud.update] &&
                !checkButtonState &&
                __?.bank_account_name !== 'DEPOSIT' && (
                  <Button
                    size='small'
                    type='primary'
                    onClick={async () => {
                      setEditOrAdd({ reason: 'EDIT', id: __.payroll_id });
                      await getForEditPayroll(__.payroll_id);
                      dispatch(setModal(true));
                    }}
                  >
                    Edit
                  </Button>
                )}

              {permission?.[crud?.delete] && (
                <>
                  {!checkButtonState ? (
                    <Popconfirm
                      onConfirm={() => {
                        if (user?.user_id) {
                          deleteEmployeeExpense({
                            body: {
                              payroll_deleted_by: user.user_id,
                              payroll_account_id: __.payroll_account_id,
                            },
                            id: __.payroll_id,
                          });
                          setColumnIndex(index);
                        }
                      }}
                      title='Sure to delete?'
                    >
                      <Button
                        size='small'
                        type='primary'
                        danger
                        loading={columnIndex === index && true && deleteIsLoading}
                      >
                        Delete
                      </Button>
                    </Popconfirm>
                  ) : (
                    ''
                  )}
                </>
              )}
            </Space>
          </>
        );
      },
    },
  ];

  return (
    <PayRollStyle>
      <BreadCrumb arrOfOption={['Payroll', 'Payroll']} />
      <Row justify={'space-between'} style={{ marginBottom: '1rem' }}>
        {permission?.[crud.create] && (
          <AddEmployeeExpense
            setEditOrAdd={setEditOrAdd}
            editOrAdd={editOrAdd}
            getForEditData={getForEditData}
          />
        )}

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <Col xs={24} sm={12} md={8} lg={8}>
                <DatePicker.RangePicker
                  name={'date_range'}
                  presets={rangePresets}
                  format={'YYYY-MM-DD'}
                  onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
                />
              </Col>
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
              <Reload
                refetch={() => {
                  getAllRefetch();
                }}
              />
            </Row>
          </Form>
        </Col>
      </Row>

      <div>
        <span style={{ marginLeft: '5px', float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count || data?.data?.length || 0}</strong> rows.
        </span>
        <Table
          scroll={{ x: 'max-content' }}
          size='small'
          bordered
          rowKey={(e) => e.payroll_id}
          columns={columns}
          dataSource={dataWithKey}
          loading={{
            spinning: dataIsLoading || isFetching,
            indicator: loadingIndicator,
          }}
          pagination={changePagination(data?.count)}
        />
      </div>
    </PayRollStyle>
  );
}

const PayRollStyle = styled.div``;
